// @flow

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { colors } from '../constants';
import { PlayIcon, StopIcon, TrashCanIcon } from '../icons';
import IconButton from './icon_button';
import { Label } from '../typography';

import { PulseAnimation } from '../animations';

type Props = {
  sourceUrl: string,
  compact: boolean,
  playErrorCallback: (string) => void,
  deleteCallback: (SyntheticEvent<HTMLButtonElement>) => void,
  fileName: string,
};

const AudioPlayer = (props: Props) => {
  const [status, setStatus] = useState('stopped');
  const [player, setPlayer] = useState();
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (player !== undefined) console.log(`🎵 Audio Player: ${status}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    if (player !== undefined) {
      player.addEventListener('ended', stopAudio);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player]);

  const playAudio = () => {
    if (player === undefined) return null;

    const playerPromise = player.play();

    // Some browsers don't return a Promise from play()
    if (playerPromise !== undefined) {
      playerPromise
        .then(() => setStatus('playing'))
        .catch((error) => props.playErrorCallback(error));
    } else {
      setStatus('playing');
    }
  };

  const stopAudio = () => {
    if (player === undefined) return null;

    player.pause();
    player.currentTime = 0;
    setStatus('stopped');
  };

  const togglePlaying = (event) => {
    event && event.preventDefault();

    isPlaying() ? stopAudio() : playAudio();
  };

  const isPlaying = () => status === 'playing';

  const extraControls = () => (
    <>
      {isPlaying() ? (
        <Playing>Playing your recording...</Playing>
      ) : (
        <FileName
          href={props.sourceUrl}
          target="_blank"
          onClick={togglePlaying}
          data-provides="file-name"
          isHovered={isHovered}
        >
          {props.fileName}
        </FileName>
      )}

      {!isPlaying() && (
        <IconButton
          label={'Delete'}
          size={'medium'}
          Icon={TrashCanIcon}
          onClick={props.deleteCallback}
        />
      )}
    </>
  );

  const Icon = isPlaying() ? StopIcon : PlayIcon;

  return (
    <>
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <PlaybackButton
          onClick={togglePlaying}
          compact={props.compact}
          isHovered={isHovered}
          data-provides="playback-control"
          data-provides-of={status}
        >
          <Label>{isPlaying() ? 'Stop' : 'Play'}</Label>
          <Icon />
        </PlaybackButton>

        {!props.compact && props.sourceUrl && extraControls()}
      </div>

      <audio
        controls={false}
        src={props.sourceUrl}
        // $FlowFixMe
        ref={(ref) => setPlayer(ref)}
        type="audio/mp3"
      />
    </>
  );
};

AudioPlayer.defaultProps = {
  compact: true,
  playErrorCallback: (error) => console.error(error),
  deleteCallback: () => {},
  fileName: 'Anonymous.mp3',
};

const PlaybackButton = styled.div`
  position: relative;
  background: ${colors.darkGreenhouseGreen};

  border-radius: 50%;
  cursor: pointer;
  display: inline-block;
  vertical-align: middle;
  box-shadow: 0 0 0 rgba(36, 164, 127, 0.4);
  will-change: box-shadow; // Performance optimization
  width: 32px;
  height: 32px;

  ${Label} {
    display: none;
  }

  svg {
    display: block;
    position: relative;
    top: 50%;
    left: 50%;
    transform: translate(-49%, -51%);
    ${(props) =>
      props['data-provides-of'] === 'playing' &&
      `transform: translate(-49%, -50%);`}
  }

  ${(props) =>
    props.compact &&
    `width: 24px; height: 24px; svg { width: 15px; height: 15px; transform: translate(-51%, -52%);}`}

  ${(props) =>
    props.isHovered && `background: ${colors.mediumGreenhouseGreen};`}

  ${(props) => props['data-provides-of'] === 'playing' && PulseAnimation}
`;

const FileName = styled.a`
  padding: 7px 15px;
  display: inline-block;
  vertical-align: middle;
  text-decoration: none;
  color: ${colors.darkGrey};

  &:visited {
    color: ${colors.darkGrey};
  }

  ${(props) => props.isHovered && `text-decoration: underline;`}
`;

const Playing = styled.span`
  margin: 0 15px;
  display: inline-block;
  vertical-align: middle;
  font-style: italic;
`;
Playing.displayName = 'Playing';

export default AudioPlayer;
